import React from "react";
import { Link } from "react-router-dom";
import { useB2BTranslation } from "../B2BShared/hooks/useB2BTranslation";
import { useB2BAuth } from "../B2BShared/B2BAuthContext/B2BAuthContext";
import { LogOut } from "lucide-react";
import { GlassButton } from "../B2BShared/GlassButton/GlassButton";
import { B2B_EVENTS, isEventExpired } from "../B2BShared/constants/events";
import "./B2BSection.css";

const B2BSection: React.FC = () => {
  const { t, tAuth } = useB2BTranslation();
  const { isAuthenticated, user, logout } = useB2BAuth();
  const upcomingEvents = B2B_EVENTS;

  const scrollToEvents = (): void => {
    const eventsElement = document.getElementById("events");
    if (eventsElement) {
      eventsElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleLogout = (): void => {
    logout();
  };

  return (
    <div className="b2b-section-new">
      {/* Header Section */}
      <header className="b2b-header">
        <div className="b2b-container">
          <div className="b2b-hero-content">
            <p className="b2b-hero-description">{t("section.description")}</p>

            <div className="b2b-hero-actions">
              {isAuthenticated ? (
                <>
                  <Link to="/gcb/dashboard">
                    <GlassButton
                      variant="primary"
                      size="medium"
                    >
                      {t("dashboard.welcome")}, {user?.name || "User"}!
                    </GlassButton>
                  </Link>
                  <GlassButton
                    variant="outline"
                    size="medium"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    {tAuth("logout")}
                  </GlassButton>
                </>
              ) : (
                <>
                  <Link to="/gcb/login">
                    <GlassButton
                      variant="outline"
                      size="medium"
                    >
                      {t("common.login")}
                    </GlassButton>
                  </Link>

                  <Link
                    to="/gcb/register"
                    className="b2b-register-link"
                  >
                    {t("common.register")}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Services Section */}
      <section className="b2b-services">
        <div className="b2b-container">
          {/* B2B Events Banner */}
          <div
            className="b2b-events-banner"
            onClick={scrollToEvents}
          >
            <div className="b2b-banner-content">
              <span className="b2b-banner-text">{t("section.seeEvents")}</span>
              <span className="b2b-banner-arrow">→</span>
            </div>
          </div>

          {/* Corporate Payments */}
          <div className="b2b-service-card">
            <h3 className="b2b-service-title">
              {t("section.services.corporatePayments.title")}
            </h3>
            <p className="b2b-service-description">
              {t("section.services.corporatePayments.description")}
            </p>
            <a
              href="https://gcbusiness.space/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <GlassButton
                variant="primary"
                size="medium"
              >
                {t("section.learnMore")}
              </GlassButton>
            </a>
          </div>

          {/* Other Services */}
          <div className="b2b-service-card">
            <h3 className="b2b-service-title">
              {t("section.services.otherServices.title")}
            </h3>
            <p className="b2b-service-description">
              {t("section.services.otherServices.description")}
            </p>
            <Link to="/gcb/contact">
              <GlassButton
                variant="primary"
                size="medium"
              >
                {t("section.services.otherServices.buttonText")}
              </GlassButton>
            </Link>
          </div>
        </div>
      </section>

      {/* Events List Section */}
      <section
        id="events"
        className="b2b-events-section"
      >
        <div className="b2b-container">
          <h2 className="b2b-section-title">{t("section.events.title")}</h2>
          <p className="b2b-section-subtitle">{t("section.events.subtitle")}</p>

          {upcomingEvents.map((event) => (
            <div
              key={event.id}
              className="b2b-event-card featured"
            >
              <div className="b2b-event-header">
                <div className="b2b-event-category">{event.category}</div>
                <div className="b2b-event-price">{event.price}</div>
              </div>
              <div className="b2b-event-content">
                <h3 className="b2b-event-title">{event.title}</h3>
                <p className="b2b-event-description">{event.description}</p>
                <div className="b2b-event-actions">
                  {isEventExpired(event.eventDate) ? (
                    <button
                      disabled
                      className="b2b-event-register-btn b2b-event-register-btn-disabled"
                    >
                      Мероприятие завершено
                    </button>
                  ) : (
                    <a
                      href={event.registrationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="b2b-event-register-btn"
                    >
                      Зарегистрироваться
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default B2BSection;

